// Dashboard user-facing copy (W36.0).
//
// Every 繁體中文 string the mobile dashboard renders lives here, following the
// same single-source pattern as `lib/diary/mood.ts` and
// `lib/wish/blockTypeLabel.ts` (W35.7 defect #7 shipped raw English block types
// into a zh-Hant UI; this file exists so that class of bug cannot recur here).
//
// Two rules baked into the copy itself:
//
// - Mandarin-flavored 繁體中文, never 粵語 / 简体 (AGENTS.md §3 #14). The
//   partner is 「對方」, never 「TA」 in user-facing strings — the spec uses TA as
//   shorthand for the *concept*, but 「TA」 is not how the product speaks to its
//   users.
// - Copy states absence without nagging (spec §2.4 R4.1 / §2.3 R5.2). 「今天還沒記」
//   is informational; it never becomes 「你今天還沒寫日記」.

export const DASHBOARD_I18N = {
  hero: {
    title: "首頁",
    /** `{date}` is the subtitle line, e.g. 「週一 · 9 月 28 日」. */
    daysTogether: "在一起",
    daysUnit: "天",
    sinceFrom: "在一起 · 自 {date}",
  },
  message: {
    label: "今日留言",
    writeFirst: "今天還沒有留言, 寫下第一句",
    writeHint: "點一下寫今天的話",
    edit: "編輯今日留言",
    clear: "清除留言",
    clearConfirm: "清除後這一天就沒有留言了, 確定要清除嗎?",
    placeholder: "今天想對 TA 說什麼…",
    counter: "{count} / 140",
    saved: "已儲存",
    savedByPartner: "TA 剛剛更新過這則留言",
    authorSelf: "我寫的",
    authorPartner: "TA 寫的",
    review: "回顧我們的每一天",
    reviewCount: "{count} 則",
    reviewEmpty: "還沒有記錄",
  },
  mood: {
    label: "今天心情",
    /**
     * W57.0 — 「今天還沒記心情」 → 「今天還沒記」.
     *
     * The sentence used to be long because the card carried the meaning by
     * itself. It does not any more: the mood is now **two separate cards**,
     * each headed by a person, and the section around them is named
     * 「今天心情」 (`mood.label`). So the pill only has to say the state —
     * 「今天還沒記」 — and the header says *who* has not checked in.
     *
     * **The two cards say the same sentence on purpose.** They used to be two
     * columns of one card, which meant the column header had to disambiguate
     * them; as two separate cards the headers do that job, and a second
     * wording would only add a distinction the layout already carries.
     */
    notCheckedIn: "今天還沒記",
    pickerTitle: "今天的心情",
    pickerHint: "選一個最接近的就好",
  },
  anniversary: {
    celebrate: "今天是 {title}",
    countdown: "下一個紀念日",
    /** `{count}` is substituted here — so this label **carries the number**. */
    inDays: "還有 {count} 天",
    /**
     * The unit alone. W47.4 / `ADR-0138`.
     *
     * The countdown state renders `days_until` as a display-2 numeral and then
     * used to pass this card's label `inDays`, which substitutes the same number
     * a second time — the screen read `288  還有 288 天`, two countdowns wearing
     * two different sizes. Any future edit that reaches for `inDays` in the
     * countdown state reintroduces that; gate G15 exists to stop it.
     */
    inDaysUnit: "天後",
    /** The ring's screen-reader name. `{pct}` is the visible percentage. */
    cycleProgress: "距離上次紀念日已過 {pct}%",
    today: "就是今天",
    seal: "時間",
  },
  activity: {
    /**
     * W47.1: `activity.label` is **deleted**, not renamed.
     *
     * It used to be the mood card's visible heading while the same card's
     * `AccentCard` aria-label said 「今日狀態」 — a screen reader and an eye told
     * two different things about one card, and neither matched its contents (two
     * mood check-ins). The mood card now says 「今天心情」 (`mood.label`) and
     * `modules.label` owns 「最新動態」, which is what each actually describes.
     *
     * **Leaving the key behind would have been the actual bug.** A dead key
     * holding the same copy as `modules.label`, under a comment claiming it had
     * moved, is an invitation to wire a second card to the same name — and the
     * collision `G6` exists to prevent is precisely two 「最新動態」 on one
     * screen. `activity.empty` stays: it has two consumers.
     */
    empty: "還沒有動態",
  },
  weather: {
    /**
     * W47.1. Every string here is 繁體中文 (`AGENTS.md §3 #14`); the codes and
     * the emoji arrive from the backend, which owns the WMO table.
     */
    eyebrow: "天氣",
    /** Rendered next to the place name when the coordinates were not supplied. */
    fallbackBadge: "未授權 · 顯示香港",
    /** The badge becomes a button that asks the browser again. */
    locating: "取得位置中…",
    /**
     * W47.7 — what the browser **said**, after the user pressed the badge.
     *
     * The card used to show 「未授權 · 顯示香港」 before the press and the same
     * string afterwards, so a press that was refused, a press that could not find
     * a position, and a press that ran out of time were indistinguishable — and
     * all three read as 「失敗」. `getCurrentPosition` hands back an error code
     * for exactly this and it was being discarded.
     *
     * Only `denied` names a cause. `timeout` and `unavailable` are the phone
     * failing to find a fix, and telling somebody their *permission* was refused
     * when it was not would be a second lie on top of the first.
     */
    reasonDenied: "位置被拒絕 · 請到瀏覽器設定開啟",
    reasonTimeout: "定位逾時 · 再試一次",
    reasonUnavailable: "暫時定位不到",
    reasonUnsupported: "這個瀏覽器不支援定位",
    /** Shown when the upstream could not be reached. The card keeps its slot. */
    unavailable: "暫時取不到天氣",
    feels: "體感 {value}",
    humidity: "濕度 {value}",
    today: "今日",
    rainChance: "降雨機率 {value}",
    /** `{high}` / `{low}` / `{probability}` — one decimal on the temperatures. */
    todayRange: "{high} / {low}",
    /** `{day}` is 明天 / 週六 / … ; `{probability}` is a whole percent. */
    dayRange: "{low} · 雨 {probability}",
    /** CC BY 4.0 requires the credit; it is a licence obligation, not a nicety. */
    credit: "天氣數據由 {source} 提供 · CC BY 4.0",
    notCheckedIn: "未記",
  },
  modules: {
    label: "最新動態",
    diary: "日記",
    anniversary: "紀念日",
    calendar: "行事曆",
    album: "相簿",
    wishlist: "心願",
    daily_message: "每日留言",
    todayBadge: "今天 {count}",
    loadFailed: "暫時無法載入",
    detail: "查看詳情",
    /** Sheet body when a module has no row-level content. */
    emptySheet: "這裡還沒有內容",
    /**
     * W51 (D-1): who wrote this. There are only two people, so the feed names
     * a person or says nothing — it never says 「對方」 for a row the system
     * produced (the anchor anniversary has `actor_id: null`). `ADR-0079`.
     */
    actorMe: "我",
    actorPartner: "對方",
  },
  notification: {
    label: "通知",
    title: "站內通知",
    empty: "沒有新的通知",
    markAllSeen: "全部已讀",
    unreadOne: "{editor} 更新了 {date} 的留言",
    unreadFallback: "有新的留言更新",
    openBell: "開啟通知",
  },
  empty: {
    title: "還沒有配對, 來邀請你的伴侶嗎?",
    description: "配對完成後, 你們的共享空間才會開始累積天數。",
    cta: "去配對",
  },
  /**
   * W47.2 — the 「+ 新增」 action panel.
   *
   * The button said 「新增」 and navigated to `/diaries/new`, so the label promised
   * anything and delivered a diary, with no step telling the person what had just
   * happened. Three targets have a dedicated `/new` route; 相簿 has none (a photo
   * is a block inside a wish or a diary) and is deliberately absent rather than
   * given a row that would land on a module's front page and call itself a
   * create action.
   */
  create: {
    title: "新增",
    hint: "想記下什麼",
    diary: { label: "寫日記", hint: "今天發生了什麼" },
    wish: { label: "許一個心願", hint: "想一起做到的事" },
    anniversary: { label: "加一個紀念日", hint: "要記住的那一天" },
    event: { label: "加一個行程", hint: "在日曆上" },
  },
  error: {
    title: "首頁載入失敗",
    retry: "重試",
  },
} as const;

/**
 * The 12 fixed quick-insert emoji for the message editor.
 *
 * Deliberately NOT a full picker (W36.0 decision): the spec left this open as
 * §10 #4, and a 12-slot tray keeps the editor a single sheet with one job. The
 * text field still accepts any emoji typed or pasted directly, so nothing is
 * actually unreachable — only the *convenience* set is bounded.
 *
 * These are **data inside a text field**, not UI icons, so AGENTS.md §3 #19's
 * emoji ban does not apply (same exemption as diary mood). They are rendered
 * with an explicit Apple emoji font stack so Windows/Android do not fall back
 * to monochrome glyphs.
 */
export const QUICK_EMOJI: readonly string[] = Object.freeze([
  "❤️", "🥰", "😊", "😂", "😢", "😡",
  "👍", "🙏", "🎉", "🌧", "☕", "🏠",
]);

